// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppIcon, IconButton, Spinner, useToast } from '@loa/ui-components';
import { quoteApi } from '../../../api/quoteApi';
import type { Quote, QuoteOption, QuoteOptionsResponse, QuoteType } from '../../../types/quote';
import {
  COVER_TYPE_LABELS,
  INVESTMENT_TYPE_LABELS,
  PENSION_TYPE_LABELS,
  PROTECTION_TYPE_LABELS,
  productTypeLabel,
} from '../../../types/quote';
import { QuoteOptionCard } from '../../quotes/QuoteOptionCard';
import '../../quotes/QuoteOptionCard.css';

const POLL_INTERVAL_MS = 3000;
const PAGE_SIZE = 3;

type Slot =
  | { kind: 'option'; option: QuoteOption }
  | { kind: 'skeleton'; key: string };

const formatAmount = (amount: number, currency: string): string =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB');
};

/** "…quote request details" header per quote type (Investment & Protection
 *  Proposals PRD — reuses this type-agnostic quote-options step for all
 *  three quote-backed proposal types). */
const DETAILS_HEADER_TITLE: Record<QuoteType, string> = {
  PENSION: 'Pension quote request details',
  INVESTMENT: 'Investment quote request details',
  PROTECTION: 'Protection quote request details',
};

const EDIT_BUTTON_LABEL: Record<QuoteType, string> = {
  PENSION: 'Edit pension quote',
  INVESTMENT: 'Edit investment quote',
  PROTECTION: 'Edit protection quote',
};

/** Type-specific detail rows (in addition to the shared Client name / Amount
 *  / Client DOB / Special requests rows). */
function typeSpecificDetails(quote: Quote): ReadonlyArray<{ label: string; value: string }> {
  switch (quote.quoteType) {
    case 'PENSION':
      return [
        {
          label: 'Pension Type',
          value: productTypeLabel(quote.pensionType, PENSION_TYPE_LABELS as Record<string, string>, '—'),
        },
        { label: 'NRA', value: quote.normalRetirementAge?.toString() ?? '—' },
      ];
    case 'INVESTMENT':
      return [
        {
          label: 'Investment Type',
          value: productTypeLabel(quote.investmentType, INVESTMENT_TYPE_LABELS as Record<string, string>, '—'),
        },
        {
          label: 'Government Levy 1% Covered',
          value: quote.governmentLevyCovered ? 'Yes' : 'No',
        },
      ];
    case 'PROTECTION':
      return [
        {
          label: 'Protection Type',
          value: productTypeLabel(quote.protectionType, PROTECTION_TYPE_LABELS as Record<string, string>, '—'),
        },
        {
          label: 'Cover Type',
          value: quote.coverType ? COVER_TYPE_LABELS[quote.coverType] ?? quote.coverType : '—',
        },
        {
          label: 'Term of Cover',
          value: quote.termOfCover != null ? `${quote.termOfCover} years` : '—',
        },
      ];
    default:
      return [];
  }
}

export type WizardStepQuoteOptionsProps = Readonly<{
  quoteId: string;
  /** The wizard returns to Step 1 (edit mode) with the freshly-loaded quote. */
  onEditRequested: (quote: Quote) => void;
  onSaveAndClose: () => void;
  /** Advances the wizard to Step 3 (Statement of Suitability). */
  onCreateStatement: (quote: Quote) => void;
  /**
   * Primary CTA label. Defaults to the v1 wizard's "Create Statement Of
   * Suitability"; the sectioned builder passes "Continue to Builder".
   */
  createStatementLabel?: string;
  /**
   * Fired with the refreshed quote after a select/deselect round-trip so an
   * embedding shell (the sectioned builder's Quote Options rail section) can
   * track `selectedOptionId` for its completion tick. Optional — the v1
   * wizard doesn't need it.
   */
  onQuoteChanged?: (quote: Quote) => void;
}>;

/**
 * Step 2 of the pension proposal wizard (Figma 1453-62322): the quote
 * options comparison surface. Reuses the existing {@link QuoteOptionCard}
 * carousel and polls `GET /api/quotes/:id/options` until every provider has
 * responded, exactly like the standalone Quotes-tab options page.
 */
export function WizardStepQuoteOptions({
  quoteId,
  onEditRequested,
  onSaveAndClose,
  onCreateStatement,
  createStatementLabel = 'Create Statement Of Suitability',
  onQuoteChanged,
}: WizardStepQuoteOptionsProps) {
  const { showToast } = useToast();

  const [quote, setQuote] = useState<Quote | null>(null);
  const [optionsResponse, setOptionsResponse] = useState<QuoteOptionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);

  // One-shot quote fetch on mount (and whenever quoteId changes, e.g. after
  // an edit round-trip that keeps the same quote id).
  useEffect(() => {
    if (!quoteId) return undefined;
    let cancelled = false;
    quoteApi
      .getQuote(quoteId)
      .then((q) => {
        if (!cancelled) setQuote(q);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load quote');
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId]);

  // Polling loop for provider options. Stops once status === COMPLETED.
  const pollTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!quoteId) return undefined;
    let cancelled = false;

    const fetchOptions = async () => {
      try {
        const response = await quoteApi.getQuoteOptions(quoteId);
        if (cancelled) return;
        setOptionsResponse(response);
        if (response.status === 'COMPLETED' && pollTimerRef.current !== null) {
          window.clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      } catch {
        // Swallow polling errors — surface only the initial-load error.
      }
    };

    void fetchOptions();
    pollTimerRef.current = window.setInterval(fetchOptions, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [quoteId]);

  const handleSelect = useCallback(
    async (option: QuoteOption) => {
      setSelectingId(option.id);
      const wasSelected = option.selected;
      try {
        if (wasSelected) {
          await quoteApi.deselectOption(quoteId, option.id);
        } else {
          await quoteApi.selectOption(quoteId, option.id);
        }
        const refreshed = await quoteApi.getQuoteOptions(quoteId);
        setOptionsResponse(refreshed);
        const refreshedQuote = await quoteApi.getQuote(quoteId);
        setQuote(refreshedQuote);
        onQuoteChanged?.(refreshedQuote);
        showToast(
          wasSelected ? 'Quote option deselected' : 'Quote option selected',
          'success',
        );
      } catch {
        showToast(
          wasSelected ? 'Failed to deselect quote option' : 'Failed to select quote option',
          'error',
        );
      } finally {
        setSelectingId(null);
      }
    },
    [quoteId, showToast, onQuoteChanged],
  );

  const handlePin = useCallback(
    async (option: QuoteOption) => {
      const wasPinned = option.pinned;
      try {
        if (wasPinned) {
          await quoteApi.unpinOption(quoteId, option.id);
        } else {
          await quoteApi.pinOption(quoteId, option.id);
        }
        const refreshed = await quoteApi.getQuoteOptions(quoteId);
        setOptionsResponse(refreshed);
      } catch {
        showToast(
          wasPinned ? 'Failed to unpin quote option' : 'Failed to pin quote option',
          'error',
        );
      }
    },
    [quoteId, showToast],
  );

  const handleEditClick = useCallback(() => {
    if (quote) onEditRequested(quote);
  }, [quote, onEditRequested]);

  const handleCreateStatement = useCallback(() => {
    if (quote) onCreateStatement(quote);
  }, [quote, onCreateStatement]);

  const slots = useMemo<Slot[]>(() => {
    if (!optionsResponse) return [];
    const sortedOptions = [...optionsResponse.options].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      if (a.pinned && b.pinned) return a.providerName.localeCompare(b.providerName);
      return a.receivedAt.localeCompare(b.receivedAt);
    });
    const optionSlots: Slot[] = sortedOptions.map((option) => ({ kind: 'option', option }));
    const skeletonCount = Math.max(
      0,
      optionsResponse.expectedProviderCount - optionsResponse.options.length,
    );
    const skeletonSlots: Slot[] = Array.from({ length: skeletonCount }, (_, i) => ({
      kind: 'skeleton',
      key: `skeleton-${i}`,
    }));
    return [...optionSlots, ...skeletonSlots];
  }, [optionsResponse]);

  const totalPages = Math.max(1, Math.ceil(slots.length / PAGE_SIZE));

  useEffect(() => {
    if (pageIndex >= totalPages) setPageIndex(0);
  }, [pageIndex, totalPages]);

  const currentSlots = useMemo(
    () => slots.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE),
    [slots, pageIndex],
  );

  const canGoPrev = pageIndex > 0;
  const canGoNext = pageIndex < totalPages - 1;

  if (error) {
    return (
      <div className="quote-options-page__card" data-testid="wizard-step-quote-options-error">
        {error}
      </div>
    );
  }

  if (!quote || !optionsResponse) {
    return (
      <div
        className="quote-options-page__card"
        style={{ display: 'flex', justifyContent: 'center' }}
        data-testid="wizard-step-quote-options-loading"
      >
        <Spinner />
      </div>
    );
  }

  const extraDetails = typeSpecificDetails(quote);

  return (
    <div data-testid="wizard-step-quote-options">
      <section className="quote-options-page__card">
        <div className="quote-options-page__details-header">
          <h2 className="quote-options-page__card-title" style={{ margin: 0 }}>
            {DETAILS_HEADER_TITLE[quote.quoteType] ?? 'Quote request details'}
          </h2>
          <IconButton
            variant="square-outlined"
            size="md"
            onClick={handleEditClick}
            icon={<AppIcon name="edit" />}
            aria-label={EDIT_BUTTON_LABEL[quote.quoteType] ?? 'Edit quote'}
            data-testid="wizard-quote-edit-btn"
          />
        </div>
        <div className="quote-options-page__details-grid">
          <Detail label="Client name" value={quote.clientNames} />
          <Detail label="Amount" value={formatAmount(quote.amount, quote.currencyCode)} />
          <Detail label="Client DOB" value={formatDate(quote.clientDob)} />
          {extraDetails.map((row) => (
            <Detail key={row.label} label={row.label} value={row.value} />
          ))}
          <Detail label="Special requests" value={quote.specialRequests || '—'} />
        </div>
      </section>

      <section className="quote-options-page__card">
        <div className="quote-options-page__cards-header">
          <h2 className="quote-options-page__card-title" style={{ margin: 0 }}>
            Quote options
          </h2>
          <div className="quote-options-page__page-nav">
            <button
              type="button"
              className="quote-options-page__page-btn"
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
              disabled={!canGoPrev}
              aria-label="Previous quote options"
              data-testid="wizard-quote-options-prev"
            >
              ‹
            </button>
            <button
              type="button"
              className="quote-options-page__page-btn"
              onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
              disabled={!canGoNext}
              aria-label="Next quote options"
              data-testid="wizard-quote-options-next"
            >
              ›
            </button>
          </div>
        </div>
        <div className="quote-options-page__cards">
          {currentSlots.map((slot) =>
            slot.kind === 'option' ? (
              <QuoteOptionCard
                key={slot.option.id}
                option={slot.option}
                selecting={selectingId !== null}
                onSelect={() => handleSelect(slot.option)}
                pinned={slot.option.pinned}
                onPin={() => handlePin(slot.option)}
              />
            ) : (
              <div
                key={slot.key}
                className="quote-options-page__skeleton"
                data-testid="wizard-quote-option-skeleton"
              />
            ),
          )}
        </div>
      </section>

      <div className="quote-options-page__footer">
        <button
          type="button"
          className="quote-options-page__footer-btn quote-options-page__footer-btn--secondary"
          onClick={onSaveAndClose}
        >
          Save And Close
        </button>
        <button
          type="button"
          className="quote-options-page__footer-btn quote-options-page__footer-btn--primary"
          onClick={handleCreateStatement}
          disabled={!quote.selectedOptionId}
        >
          {createStatementLabel}
        </button>
      </div>
    </div>
  );
}

function Detail({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div>
      <span className="quote-options-page__detail-label">{label}</span>
      <span className="quote-options-page__detail-value">{value}</span>
    </div>
  );
}

export default WizardStepQuoteOptions;
